import * as XLSX from "xlsx";

import { ExportableArray, ExportMetadata } from "./export";
import { formatDateTimeFast } from "./formatting";
import { assertNever } from "./obj";

const NUMBER_OF_BLANK_ROWS_BEFORE_FOOTER = 2;

function tableToWorksheet(
    exportableArray: ExportableArray,
    columnsWidth: Array<number> | undefined,
    metadata: ExportMetadata
): XLSX.WorkSheet {
    const { header, content } = exportableArray;
    let cellCount: number;
    if (header) {
        cellCount = header.length;
    } else if (content.length > 0) {
        cellCount = content[0].length;
    } else {
        cellCount = 0;
    }
    const rowCount = (header ? 1 : 0) + content.length;
    const range: XLSX.Range = {
        s: { c: 0, r: 0 },
        e: {
            c: cellCount,
            r: rowCount + NUMBER_OF_BLANK_ROWS_BEFORE_FOOTER + 1,
        },
    };
    const worksheet: XLSX.WorkSheet = {
        "!range": range,
        "!ref": XLSX.utils.encode_range(range.s, range.e),
    };
    if (columnsWidth) {
        worksheet["!cols"] = columnsWidth.map((w) => {
            return { wpx: w };
        });
    }
    let headerRowPos: number;
    if (header) {
        // Write the header
        header.forEach((headerValue, colIndex) => {
            const cell = toStringCell(headerValue, "bold");
            if (cell) {
                worksheet[XLSX.utils.encode_cell({ c: colIndex, r: 0 })] = cell;
            }
            // else don't write empty strings
        });
        // Need to offset the content by 1 since we have the header.
        headerRowPos = 1;
    } else {
        headerRowPos = 0;
    }
    // Write the content
    content.forEach((row, rowIndex) => {
        row.forEach((value, colIndex) => {
            if (value === undefined || value === null) {
                return;
            }
            let cell: XLSX.CellObject | undefined;
            if (typeof value === "number") {
                cell = toNumberCell(value);
            } else if (typeof value === "string") {
                cell = toStringCell(value);
            } else {
                throw new Error("Unsupported type " + typeof value);
            }
            if (cell !== undefined) {
                worksheet[
                    XLSX.utils.encode_cell({
                        c: colIndex,
                        r: rowIndex + headerRowPos,
                    })
                ] = cell;
            }
        });
    });
    // Write the footer
    setWorksheetFooter(
        worksheet,
        headerRowPos + content.length + NUMBER_OF_BLANK_ROWS_BEFORE_FOOTER,
        metadata
    );
    return worksheet;
}

function workbookWithSingleWorksheet(
    sheetName: string,
    worksheet: XLSX.WorkSheet
): XLSX.WorkBook {
    const workbook: XLSX.WorkBook = {
        SheetNames: [sheetName],
        Sheets: { [sheetName]: worksheet },
    };
    return workbook;
}

function s2ab(s: string) {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i !== s.length; ++i) {
        view[i] = s.charCodeAt(i) & 0xff;
    }
    return buf;
}

function workbookToBinary(workbook: XLSX.WorkBook) {
    const wopts: XLSX.WritingOptions = {
        bookType: "xlsx",
        bookSST: false,
        type: "binary",
    };
    const wbout: string = XLSX.write(workbook, wopts);
    return s2ab(wbout);
}

function worksheetToCSVBinary(workbook: XLSX.WorkSheet): string {
    return XLSX.utils.sheet_to_csv(workbook, undefined);
}

type CellType = "string" | "number";

/* For exporting an array */
interface ExportArrayColumn<ROW> {
    label: string;
    getData: (row: ROW, rowIndex: number) => string | null;
    width?: number;
    type: CellType;
}

const DEFAULT_COLUMN_WIDTH_IN_PIXELS = 100;

function arrayToWorksheet<ROW>(
    array: Array<ROW>,
    exportColumns: Array<ExportArrayColumn<ROW>>,
    metadata: ExportMetadata
) {
    const range: XLSX.Range = {
        s: { c: 0, r: 0 },
        e: {
            c: exportColumns.length,
            r: array.length + 1 + NUMBER_OF_BLANK_ROWS_BEFORE_FOOTER + 1,
        },
    };
    const worksheet: XLSX.WorkSheet = {
        "!range": range,
        "!ref": XLSX.utils.encode_range(range.s, range.e),
    };

    const colWidths: Array<{ wpx: number }> = [];
    exportColumns.forEach((exportColumn, colIndex) => {
        // Add a ! here because we know that as exportColumn.label may not be undefined, the result cell is also defined
        worksheet[XLSX.utils.encode_cell({ c: colIndex, r: 0 })] = toStringCell(
            exportColumn.label,
            "bold"
        )!;
        const colWidth =
            exportColumn.width !== undefined
                ? exportColumn.width
                : DEFAULT_COLUMN_WIDTH_IN_PIXELS;
        colWidths.push({ wpx: colWidth });
    });
    worksheet["!cols"] = colWidths;
    array.forEach((row, rowIndex) => {
        exportColumns.forEach((exportColumn, colIndex) => {
            const value = exportColumn.getData(row, rowIndex);
            if (value === null || value === undefined) {
                return;
            }
            let cell: XLSX.CellObject | undefined;
            if (exportColumn.type === "number") {
                cell = toNumberCell(value);
            } else if (exportColumn.type === "string") {
                cell = toStringCell(value);
            } else {
                throw new Error("Unsupported type " + exportColumn.type);
            }
            if (cell) {
                worksheet[
                    XLSX.utils.encode_cell({ c: colIndex, r: rowIndex + 1 })
                ] = cell;
            }
        });
    });
    setWorksheetFooter(
        worksheet,
        1 + array.length + NUMBER_OF_BLANK_ROWS_BEFORE_FOOTER,
        metadata
    );
    return worksheet;
}

function setWorksheetFooter(
    worksheet: XLSX.WorkSheet,
    footerRowIndex: number,
    metadata: ExportMetadata
) {
    worksheet[XLSX.utils.encode_cell({ c: 0, r: footerRowIndex })] =
        toStringCell(
            `${metadata.organization.name} - ${formatDateTimeFast(
                metadata.date
            )} - ${metadata.user.email}`
        )!;
}

function toNumberCell(value: number | string): XLSX.CellObject {
    return { v: value, t: "n" };
}

function toStringCell(
    value: string,
    style: "bold" | "default" = "default"
): XLSX.CellObject | undefined {
    if (value.length === 0) {
        return undefined;
    }
    // If the value starts with an apostrophe or an equal sign
    // or if the value is a number (the regexp could probably be tweaked better)
    let quotePrefix: number | undefined;
    if (/^['=+\-@0-9]/.test(value)) {
        quotePrefix = 1;
    } else {
        quotePrefix = undefined;
    }
    let font: { bold: boolean } | undefined;
    switch (style) {
        case "default":
            font = undefined;
            break;
        case "bold":
            font = { bold: true };
            break;
        default:
            return assertNever(style);
    }
    return {
        v: value,
        t: "s",
        s: {
            quotePrefix,
            font,
        },
    };
}

export {
    type ExportArrayColumn,
    type CellType,
    arrayToWorksheet,
    tableToWorksheet,
    workbookWithSingleWorksheet,
    workbookToBinary,
    worksheetToCSVBinary,
};
